import React, { useState, useMemo } from "react";
import { useNavigate }              from "react-router-dom";
import {
    Box, Typography, TextField, Button, Alert, Stack,
    InputAdornment, IconButton, List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import AuthLayout from "./AuthLayout";
import VisibilityIcon    from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import axios from "../api/axiosConfig";

function getFriendlyRegisterError(errorCode, t) {
    if (!errorCode) return t('auth.register_failed');
    const s = errorCode.toLowerCase();
    if (s.includes('already registered') || s.includes('already exists') || s.includes('duplicate'))
        return t('auth.email_already_registered', 'An account with this email already exists.');
    if (s.includes('too many') || s.includes('rate limit'))
        return t('auth.too_many_attempts', 'Too many attempts. Please wait a moment and try again.');
    if (s.includes('invalid email') || s.includes('email'))
        return t('auth.invalid_email', 'Please enter a valid email address.');
    return t('auth.register_failed');
}

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
        <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
        <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
        <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
    </svg>
);

export default function Register() {
    const { t }    = useTranslation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: "", phone: "", email: "", password: "", confirmPassword: "", idNumber: ""
    });
    const [error,               setError]               = useState('');
    const [loading,             setLoading]             = useState(false);
    const [showPassword,        setShowPassword]        = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const pwdReqs = useMemo(() => [
        { label: t('auth.req_length',    '8+ characters'),          met: formData.password.length >= 8 },
        { label: t('auth.req_uppercase', 'Uppercase letter (A-Z)'), met: /[A-Z]/.test(formData.password) },
        { label: t('auth.req_lowercase', 'Lowercase letter (a-z)'), met: /[a-z]/.test(formData.password) },
        { label: t('auth.req_number',    'Number (0-9)'),           met: /[0-9]/.test(formData.password) },
        { label: t('auth.req_special',   'Special character (!@#…)'), met: /[^A-Za-z0-9]/.test(formData.password) },
    ], [formData.password, t]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        if (!pwdReqs.every(r => r.met)) {
            setError(t('auth.password_requirements_unmet', 'Please fulfil all password requirements.'));
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError(t('auth.passwords_mismatch'));
            return;
        }

        setLoading(true);
        try {
            await axios.post("/api/app_user/register", formData);
            navigate("/confirm-email", { state: { email: formData.email } });
        } catch (err) {
            const errorCode = err.response?.data?.error;
            setError(getFriendlyRegisterError(errorCode, t));
        } finally {
            setLoading(false);
        }
    };

    const eyeAdornment = (show, toggle) => ({
        endAdornment: (
            <InputAdornment position="end">
                <IconButton onClick={toggle} edge="end" size="small" tabIndex={-1}
                            aria-label={show ? 'Hide password' : 'Show password'}>
                    {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
            </InputAdornment>
        ),
    });

    return (
        <AuthLayout>
            <Typography variant="h3" align="center"
                        sx={{ color: 'primary.main', fontWeight: 800, mb: 4, fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('auth.register_title')}
            </Typography>
            <Button fullWidth variant="outlined" size="large"
                    startIcon={<GoogleIcon />}
                    onClick={async () => {
                        const array = new Uint8Array(32);
                        crypto.getRandomValues(array);
                        const verifier = btoa(String.fromCharCode(...array))
                            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
                        const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
                            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                        sessionStorage.setItem('pkce_verifier', verifier);
                        window.location.href =
                            `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/authorize` +
                            `?provider=google` +
                            `&redirect_to=${encodeURIComponent(window.location.origin + '/oauth-callback')}` +
                            `&code_challenge=${challenge}` +
                            `&code_challenge_method=S256` +
                            `&prompt=select_account`;
                    }}
                    sx={{ mb: 2, py: 1.5, textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}>
                {t('auth.continue_with_google', 'Continue with Google')}
            </Button>
            <Divider sx={{ mb: 2 }}>{t('auth.or', 'or')}</Divider>

            <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label={t('auth.email')} name="email" type="email"
                                   value={formData.email} onChange={handleChange} required
                                   sx={{ flex: 2 }} />
                        <TextField fullWidth label={t('auth.username')} name="username"
                                   value={formData.username} onChange={handleChange} required
                                   sx={{ flex: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label={t('auth.phone')} name="phone"
                                   value={formData.phone} onChange={handleChange} />
                        <TextField fullWidth label={t('auth.id_number')} name="idNumber"
                                   value={formData.idNumber} onChange={handleChange} />
                    </Box>
                    <Box>
                        <TextField fullWidth label={t('auth.password')} name="password"
                                   type={showPassword ? 'text' : 'password'}
                                   value={formData.password} onChange={handleChange} required
                                   InputProps={eyeAdornment(showPassword, () => setShowPassword(p => !p))} />

                        {/* Live password requirements checklist */}
                        {formData.password.length > 0 && (
                            <List dense disablePadding sx={{ mt: 0.5 }}>
                                {pwdReqs.map(req => (
                                    <ListItem key={req.label} disableGutters sx={{ py: 0 }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            {req.met
                                                ? <CheckCircleIcon fontSize="small" color="success" />
                                                : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={req.label}
                                            primaryTypographyProps={{
                                                variant: 'caption',
                                                color: req.met ? 'success.main' : 'text.secondary',
                                            }} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                    <TextField fullWidth label={t('auth.confirm_password')} name="confirmPassword"
                               type={showConfirmPassword ? 'text' : 'password'}
                               value={formData.confirmPassword} onChange={handleChange} required
                               InputProps={eyeAdornment(showConfirmPassword, () => setShowConfirmPassword(p => !p))} />
                </Stack>
                <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
                        sx={{ mt: 4, py: 1.5, fontWeight: 700, borderRadius: 2 }}>
                    {loading ? t('auth.registering') : t('auth.register_btn')}
                </Button>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.already_have')}
                        <Button size="small" component="a" href="/login"
                                sx={{ fontWeight: 700 }}>{t('auth.login_here')}</Button>
                    </Typography>
                </Box>
            </Box>
        </AuthLayout>
    );
}